/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { autocomplete, AutocompleteApi, AutocompleteState, GetSources } from '@algolia/autocomplete-js';
import { createElement, Fragment, ReactNode, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { createRoot, Root } from 'react-dom/client';
import '@algolia/autocomplete-theme-classic';
import { OnSubmitParams } from '@algolia/autocomplete-core';
import type { Render, VNode } from '@algolia/autocomplete-js';

import './AlgoliaAutocomplete.scss';
import { useAppSelector } from '../../../app/hooks';
import { AlgoliaQueryHitItem } from './Algolia_network';

const autocompleteRoots = new WeakMap<Node, Root>();

const renderWithReact18: Render = (vnode, parent) => {
  if (!(parent instanceof Element || parent instanceof DocumentFragment)) {
    throw new Error('Autocomplete can only render into an element or document fragment');
  }

  let root = autocompleteRoots.get(parent);
  if (!root) {
    root = createRoot(parent);
    autocompleteRoots.set(parent, root);
  }

  flushSync(() => {
    root.render(vnode as ReactNode);
  });
};

export type IAutocompleteComponentProps = {
  getSources: GetSources<AlgoliaQueryHitItem>;
  onSubmit: (input: OnSubmitParams<AlgoliaQueryHitItem>) => void;
  customPanelRender: (
    input: { children: VNode; render: Render; state: AutocompleteState<AlgoliaQueryHitItem> },
    root: HTMLElement
  ) => void;
  className?: string;
};

export const AutocompleteComponent = (props: IAutocompleteComponentProps) => {
  const { customPanelRender, ...rest } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  const isSearching = useAppSelector((state) => state.chat.isSearching);
  const autocompleteRef = useRef<AutocompleteApi<AlgoliaQueryHitItem> | null>(null);

  // To show the loading ring in case of search api call we are not able to update the autocomplete state directly,
  // thus we are using custom className to modify it using styles
  const hideShowLoadingIndicator = useCallback((show: boolean) => {
    const loadingIndicator = containerRef.current?.querySelector('.custom-indicator-btn') as HTMLElement | undefined;
    const submitBtn = containerRef.current?.querySelector('.custom-search-btn') as HTMLElement | undefined;

    if (show) {
      loadingIndicator?.removeAttribute('hidden');
      submitBtn?.setAttribute('hidden', '');
    } else {
      loadingIndicator?.setAttribute('hidden', '');
      submitBtn?.removeAttribute('hidden');
    }
  }, []);

  const initializeAutocomplete = () => {
    return autocomplete<AlgoliaQueryHitItem>({
      container: containerRef.current as HTMLElement,
      renderer: { createElement, Fragment, render: renderWithReact18 },
      render: customPanelRender,
      placeholder: 'Search by topic title, diagnosis, symptom, lab, image or procedure',
      autoFocus: true,
      detachedMediaQuery: 'none',
      classNames: {
        input: 'custom-search-input',
        loadingIndicator: 'custom-indicator-btn',
        label: 'custom-search-btn',
      },
      onStateChange({ state }) {
        hideShowLoadingIndicator(state.status === 'loading');
      },
      ...rest,
    });
  };

  useEffect(() => {
    if (!containerRef?.current) {
      return undefined;
    }

    const search = initializeAutocomplete();

    autocompleteRef.current = search;

    // Set the form id which is used in "Ask MedPearl" btn click after the autocomplete instance is initialized
    setTimeout(() => {
      const form = containerRef.current?.querySelector('form');
      if (form) {
        form.id = 'search-input-form';
      }
      const searchInput = containerRef.current?.querySelector('.custom-search-input') as HTMLElement | undefined;
      searchInput?.focus();
    }, 0);

    return () => {
      search.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customPanelRender, hideShowLoadingIndicator, rest]);

  useEffect(() => {
    setTimeout(() => {
      hideShowLoadingIndicator(isSearching > 0);
    }, 0);
  }, [hideShowLoadingIndicator, isSearching]);

  return (
    <div
      ref={containerRef}
      className={`form search-input-form ${props.className}`}
      data-testid="algolia-autocomplete-component"
    />
  );
};
